import { useEffect, useCallback, useRef } from 'react';
/* globals google */

const parseAddressComponents = (components) => {
  const result = {
    street1: '',
    city: '',
    state: '',
    zipcode: '',
    country: 'US',
  };

  let streetNumber = '';
  let route = '';

  components.forEach((component) => {
    const types = component.types;

    if (types.includes('street_number')) {
      streetNumber = component.long_name;
    }
    if (types.includes('route')) {
      route = component.long_name;
    }
    if (types.includes('locality')) {
      result.city = component.long_name;
    }
    // Some addresses use sublocality instead of locality
    if (types.includes('sublocality_level_1') && !result.city) {
      result.city = component.long_name;
    }
    if (types.includes('administrative_area_level_1')) {
      result.state = component.short_name;
    }
    if (types.includes('postal_code')) {
      result.zipcode = component.long_name;
    }
    if (types.includes('country')) {
      result.country = component.short_name;
    }
  });

  result.street1 = [streetNumber, route].filter(Boolean).join(' ');

  return result;
};

export default function useGooglePlaces() {
  const autocompleteRef = useRef(null);

  // Ensure Google Maps script with places library is loaded
  useEffect(() => {
    const existingScript = document.getElementById('googleMaps');

    if (existingScript) {
      // Check if places library is loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        return;
      }
      // If script exists but places not loaded, update src
      const currentSrc = existingScript.getAttribute('src');
      if (!currentSrc.includes('libraries=places')) {
        existingScript.setAttribute('src', currentSrc + '&libraries=places');
      }
      return;
    }

    const script = document.createElement('script');
    script.setAttribute('id', 'googleMaps');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&loading=async`;
    document.getElementsByTagName('body')[0].appendChild(script);
  }, []);

  const initAutocomplete = useCallback((inputElement, onPlaceSelect) => {
    if (!inputElement) return;

    // Wait for Google Maps to be ready
    const checkGoogle = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        // Clean up previous autocomplete
        if (autocompleteRef.current) {
          google.maps.event.clearInstanceListeners(autocompleteRef.current);
        }

        const autocomplete = new google.maps.places.Autocomplete(inputElement, {
          types: ['address'],
          componentRestrictions: { country: ['us', 'ca'] },
        });

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (place.address_components) {
            const parsed = parseAddressComponents(place.address_components);
            onPlaceSelect(parsed);
          }
        });

        autocompleteRef.current = autocomplete;
      } else {
        setTimeout(checkGoogle, 200);
      }
    };

    checkGoogle();
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  return { initAutocomplete };
}
